import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import Icon from '../../components/AppIcon';



import ProgressCard from './components/ProgressCard';
import AchievementBadges from './components/AchievementBadges';
import RewardsMarketplace from './components/RewardsMarketplace';
import Leaderboard from './components/Leaderboard';
import ReferralTracker from './components/ReferralTracker';
import ActiveChallenges from './components/ActiveChallenges';

const MemberGamificationHub = () => {
  const { userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [memberProgress, setMemberProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMemberProgress();
  }, []);

  const fetchMemberProgress = async () => {
    setLoading(true);
    try {
      // Mock data - replace with actual Supabase queries
      const mockProgress = {
        currentTier: 'Silver',
        currentPoints: 2450,
        nextTier: 'Gold',
        nextTierPoints: 5000,
        pointsToNextTier: 2550,
        tierProgress: 48,
        totalAchievements: 24,
        unlockedAchievements: 18,
        checkInStreak: 12,
        referralCount: 3,
        referralBonus: 300
      };
      setMemberProgress(mockProgress);
    } catch (error) {
      console.error('Error fetching member progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'achievements', label: 'Achievements', icon: 'Award' },
    { id: 'rewards', label: 'Rewards', icon: 'Gift' },
    { id: 'leaderboard', label: 'Leaderboard', icon: 'Trophy' },
    { id: 'referrals', label: 'Referrals', icon: 'Users' },
    { id: 'challenges', label: 'Challenges', icon: 'Target' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <RoleBasedHeader />
        <div className="page-content-wrapper">
          <div className="flex items-center justify-center h-[calc(100vh-64px)]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading your progress...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader />
      <div className="page-content-wrapper">
        <div className="container mx-auto px-4 py-8 max-w-7xl">
          {/* Header Section */}
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-2">
              Gamification Hub
            </h1>
            <p className="text-muted-foreground">
              Track your achievements, earn rewards, and compete with fellow members
            </p>
          </div>

          {/* Tier Progress Banner */}
          <div className="card mb-8 bg-gradient-to-r from-primary/10 via-accent/10 to-success/10 border-2 border-primary/20">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                  <Icon name="Award" size={32} color="var(--color-primary)" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-2xl font-heading font-bold text-foreground">
                      {memberProgress?.currentTier} Member
                    </span>
                    <div className="px-3 py-1 rounded-full bg-primary/20 text-primary text-sm font-medium">
                      {memberProgress?.currentPoints?.toLocaleString()} pts
                    </div>
                  </div>
                  <p className="text-muted-foreground">
                    {memberProgress?.pointsToNextTier?.toLocaleString()} points to {memberProgress?.nextTier}
                  </p>
                </div>
              </div>
              <div className="w-full md:w-64">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-muted-foreground">{memberProgress?.currentTier}</span>
                  <span className="text-muted-foreground">{memberProgress?.nextTier}</span>
                </div>
                <div className="h-3 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-primary to-accent transition-all duration-500"
                    style={{ width: `${memberProgress?.tierProgress}%` }}
                  ></div>
                </div>
                <p className="text-xs text-muted-foreground mt-1 text-right">
                  {memberProgress?.tierProgress}% complete
                </p>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="mb-6 border-b border-border overflow-x-auto">
            <div className="flex gap-2 min-w-max">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {activeTab === 'overview' && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <ProgressCard
                    title="Check-in Streak"
                    value={memberProgress?.checkInStreak}
                    unit="days"
                    icon="Calendar"
                    iconColor="var(--color-success)"
                    description="Keep it going!"
                  />
                  <ProgressCard
                    title="Achievements"
                    value={`${memberProgress?.unlockedAchievements}/${memberProgress?.totalAchievements}`}
                    icon="Award"
                    iconColor="var(--color-primary)"
                    description="Badges earned"
                  />
                  <ProgressCard
                    title="Referrals"
                    value={memberProgress?.referralCount}
                    icon="Users"
                    iconColor="var(--color-accent)"
                    description="Friends joined"
                  />
                  <ProgressCard
                    title="Bonus Points"
                    value={memberProgress?.referralBonus}
                    unit="pts"
                    icon="Gift"
                    iconColor="var(--color-warning)"
                    description="From referrals"
                  />
                </div>
                <AchievementBadges />
                <ActiveChallenges />
              </>
            )}

            {activeTab === 'achievements' && <AchievementBadges />}
            {activeTab === 'rewards' && <RewardsMarketplace currentPoints={memberProgress?.currentPoints} />}
            {activeTab === 'leaderboard' && <Leaderboard />}
            {activeTab === 'referrals' && <ReferralTracker />}
            {activeTab === 'challenges' && <ActiveChallenges />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberGamificationHub;